'use client';

import { IconYoutube } from '@vs-shop/ui/icons';
import { Effect } from 'effect';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useMediaQuery } from 'usehooks-ts';

import {
  ProductGalleryVideo,
  ProductGalleryVideoMark,
  ProductGalleryVideoMessage,
  ProductGalleryVideoMount,
  ProductGalleryVideoPoster,
  ProductGalleryVideoPosterImage,
} from '@vs-shop/commerce-ui/productGallery';

import {
  createYouTubePlayerEffect,
  extractYoutubeVideoId,
} from '../../../../../effect/utils/youtube/youtube';

type Props = {
  src: string;
  title: string;
  isActive: boolean;
};

/**
 * The YouTube video on the gallery's stage: this tenant's player, in the shared
 * stage's frame.
 *
 * What is this tenant's and stays here: that the host is YouTube, how a video id
 * and its stills are read out of an embed URL, the IFrame API player and its
 * lifecycle, and every string. What the shared parts own is the frame the player
 * mounts into, the still it replaces and the mark over that still — the things
 * that have to match the photographs beside them.
 *
 * The player is started by a shopper everywhere except a desktop-width screen
 * showing this medium, where it starts muted on its own.
 */
export function GalleryVideoMedia({ src, title, isActive }: Props) {
  const intl = useIntl();
  const [showPlayer, setShowPlayer] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const playerRef = useRef<YT.Player | null>(null);

  const isDesktop = useMediaQuery('(min-width: 1024px)');

  const videoId = extractYoutubeVideoId(src);
  const playerId = videoId ? `youtube-gallery-${videoId}` : 'youtube-gallery-invalid';

  useEffect(() => {
    if (isDesktop && isActive && videoId) {
      setShowPlayer(true);
    }
  }, [isDesktop, isActive, videoId]);

  // Stop and tear the player down once this medium is no longer the one on
  // stage: a player left running keeps playing behind the photograph beside it.
  useEffect(() => {
    if (!isActive && playerRef.current) {
      playerRef.current.destroy();
      playerRef.current = null;
      setShowPlayer(false);
    }
  }, [isActive]);

  useEffect(() => {
    if (!showPlayer || !videoId) return;

    const program = createYouTubePlayerEffect(videoId, {
      containerId: playerId,
      width: '100%',
      height: '100%',
      autoplay: true,
      mute: true,
      modestbranding: true,
      rel: 0,
      onReady: (event) => {
        event.target.playVideo();
      },
    });

    Effect.runPromise(program)
      .then((result) => {
        playerRef.current = result.player;
      })
      .catch((error: unknown) => {
        console.error('Failed to initialize YouTube player:', error);
        setLoadError(true);
      });

    return () => {
      playerRef.current?.destroy();
      playerRef.current = null;
    };
  }, [showPlayer, videoId, playerId]);

  if (!videoId) {
    return (
      <ProductGalleryVideoMessage>
        <p>
          {intl.formatMessage({
            id: 'component.client.page.product.gallery.galleryVideoMedia.invalidVideoUrl',
            defaultMessage: 'Neplatné URL videa',
          })}
        </p>
      </ProductGalleryVideoMessage>
    );
  }

  const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
  const fallbackUrl = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;

  const handleClick = () => {
    setShowPlayer(true);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setShowPlayer(true);
    }
  };

  if (showPlayer) {
    if (loadError) {
      return (
        <ProductGalleryVideoMessage>
          <p>
            {intl.formatMessage({
              id: 'component.client.page.product.gallery.galleryVideoMedia.failedToLoad',
              defaultMessage: 'Nepodařilo se načíst video',
            })}
          </p>
        </ProductGalleryVideoMessage>
      );
    }

    return (
      <ProductGalleryVideo>
        <ProductGalleryVideoMount id={playerId} />
      </ProductGalleryVideo>
    );
  }

  return (
    <ProductGalleryVideoPoster
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={handleKeyDown}
      aria-label={intl.formatMessage(
        {
          id: 'component.client.page.product.gallery.galleryVideoMedia.playVideo',
          defaultMessage: 'Přehrát video: {title}',
        },
        { title }
      )}
    >
      <ProductGalleryVideoPosterImage
        src={imgError ? fallbackUrl : thumbnailUrl}
        alt={title}
        fill
        onError={() => {
          setImgError(true);
        }}
      />
      <ProductGalleryVideoMark>
        <IconYoutube
          variant="inverted"
          aria-hidden="true"
          className="h-full w-full text-destructive drop-shadow-lg"
        />
      </ProductGalleryVideoMark>
    </ProductGalleryVideoPoster>
  );
}
